import { useCallback, useMemo } from 'react';
import type { ParamParseKey, PathMatch, PathPattern } from 'react-router';
import { matchPath, useLocation, useNavigate, useNavigationType } from 'react-router';

import { RoutePath } from 'enums/routePath';
import { getSectionPath } from 'utils/urlUtils';

export enum MatchPattern {
  ROOT_SECTION_VIEW = '/:section',
  SETTINGS_DETAILS_VIEW = `${RoutePath.SETTINGS}/:section/:itemId`,
  SPLIT_VIEW = '/:section/:itemId',
  TABLE_DETAILS_VIEW = '/:section/table/:itemId',
  TABLE_VIEW = '/:section/table',
  TOKEN = '/:section/:token',
}

/**
 * Percent-decodes each path segment, matching the normalization react-router's `useMatch` applies
 * before delegating to `matchPath`. Encoded slashes are re-escaped so they stay part of a single
 * segment. Falls back to the raw value when a segment is a malformed percent encoding.
 * @param pathname - The raw `location.pathname` to decode
 */
const decodePathname = (pathname: string): string => {
  try {
    return pathname
      .split('/')
      .map(segment => decodeURIComponent(segment).replace(/\//g, '%2F'))
      .join('/');
  } catch {
    return pathname;
  }
};

/**
 * UseRouter
 *
 * @see https://usehooks.com/useRouter/
 * @returns {object} Router object
 */
export function useRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  const sectionPath = useMemo(() => getSectionPath(location.pathname), [location.pathname]);

  /*
   * `useMatch` can't be exposed directly here for callers to invoke later (e.g. in a loop or
   * conditionally) - it's a hook, and calling it outside this hook's own top-level render would
   * violate the Rules of Hooks. `matchPath` is the non-hook function `useMatch` itself calls
   * internally, so this is a plain callback that's safe to invoke however callers need to.
   * `useMatch` matches against a percent-decoded pathname, so `decodePathname` mirrors that step -
   * without it, extracted params (`:itemId`, `:token`, ...) would stay percent-encoded.
   */
  const match = useCallback(
    <Path extends string>(pattern: Path | PathPattern<Path>): null | PathMatch<ParamParseKey<Path>> =>
      matchPath(pattern, decodePathname(location.pathname)),
    [location.pathname]
  );

  const checkIsDefaultSectionPath = useCallback((pathname: string) => pathname.split('/').length === 2, []);

  const redirectBackToSectionPath = useCallback(() => {
    window.location.assign(`${window.location.origin}${sectionPath}`);
  }, [sectionPath]);

  return useMemo(
    () => ({
      location,
      match,
      navigate,
      navigationType,
      pathname: location.pathname,
      push: navigate,
      replace: path => navigate(path, { replace: true }),
      /**
       * Checks if the given pathname is a root path.
       * A root path is defined as '/' or a path with a single segment (e.g., '/task', '/retail/', etc.).
       *
       * @param {string} pathname - The pathname to check.
       * @returns {boolean} - True if the pathname is a root path, false otherwise.
       *
       * @example
       * checkIsDefaultSectionPath('/'); // true
       * @example
       * checkIsDefaultSectionPath('/task'); // true
       * @example
       * checkIsDefaultSectionPath('/task/table/someID'); // false
       * @example
       * checkIsDefaultSectionPath('/task/someID'); // false
       */
      checkIsDefaultSectionPath,
      /**
       * A default section path, is the path to a mainSection that does not
       * indicate if it is a table or details view.
       *
       * e.g.
       * Default path: "/tasks"
       * Details path: "/tasks/someID"
       * Table path: "/tasks/table"
       */
      isDefaultSectionPath: checkIsDefaultSectionPath(location.pathname),
      /**
       * Parses the current url and returns then mainSection path.
       *
       * e.g. "/tasks/table?archived=false" => "/tasks"
       */
      sectionPath,
      /**
       * Redirects the user to a specific section path based on the current location pathname
       */
      redirectBackToSectionPath,
    }),
    [navigationType, navigate, location, match, checkIsDefaultSectionPath, sectionPath, redirectBackToSectionPath]
  );
}
